import { useQuery } from "@tanstack/react-query";
import { addMinutes } from "date-fns";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import type { CreateSurveyBulkItemDto, CreateSurveyBulkRequest, SimpleProjectDto, SystemGroupDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormDatePicker } from "@/components/Form/FormDatePicker";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Wizard, WizardStep } from "@/components/Wizard/Wizard";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Notice } from "@/design-system/Notice";
import { getEndDateAfterStartChange } from "@/helpers/date-range";
import { getFlagIcon } from "@/helpers/flags";
import { processJsonStream } from "@/helpers/processJsonStream";
import { createRequiredStringRule } from "@/helpers/rules";
import { calculateSurveyDates, calculateSurveyEndDate } from "@/helpers/survey-dates";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { usePortfolioQueries } from "@/queries/portfolio/queryOptions";
import { feelingAtHomeTemplate, type Question, surveyCategories } from "@/queries/surveys/constants";
import { surveyMutations } from "@/queries/surveys/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { CreateMassSurveyFormAudienceSelector } from "./components/CreateMassSurveyFormAudienceSelector";
import { CreateMassSurveyProgressScreen } from "./components/CreateMassSurveyProgressScreen";
import { MassSurveyFormQuestions } from "./components/MassSurveyFormQuestions";

export type CreateMassSurveyFormValues = {
  title: string;
  description: string;
  category: "other" | "feelAtHome";
  startDate: Date | undefined;
  endDate: Date | undefined;
  thankYouMessage: string;
  questions: Question[];
  group?: SystemGroupDto;
  projects: SimpleProjectDto[];
  internal: boolean;
};

const TITLE_MAX_LENGTH = 255;
const DESCRIPTION_MAX_LENGTH = 500;

export type SurveySubmissionState = {
  amountSubmitting: number;
  amountSuccess: number;
  amountFailure: number;
  totalToSubmit: number;
  completed: CreateSurveyBulkItemDto[];
  isSubmitting: boolean;
};

export function CreateMassSurveyPage(): React.ReactNode {
  const [submissionError, setSubmissionError] = useState<string>();
  const [submissionState, setSubmissionState] = useState<SurveySubmissionState>({
    amountSubmitting: 0,
    amountSuccess: 0,
    amountFailure: 0,
    totalToSubmit: 0,
    completed: [],
    isSubmitting: false,
  });
  const [isProgressScreenOpen, progressScreenHandler] = useBool(false);
  const [isCancelModalOpened, cancelModalHandler] = useBool(false);
  const [isConfirmModalOpen, confirmModalHandler] = useBool(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const portfolioQueries = usePortfolioQueries();

  const {
    data: portfolioPermissions,
    isPending: isPendingPortfolioPermissions,
    error: errorPortfolioPermissions,
  } = useQuery(portfolioQueries.permissions());

  const addMassSurveyMutation = surveyMutations.useAddMassSurvey();

  const flagIcon = getFlagIcon({
    languageId: sessionUser.uiLanguage.id,
    width: 16,
  });

  const smartDates = useMemo(() => calculateSurveyDates(), []);
  const [minStart] = useState(() => addMinutes(new Date(), 15));

  const submissionProgress = useMemo(() => {
    // Always show at least 10% progress
    if (submissionState.totalToSubmit === 0) {
      return 10;
    }

    const completedCount = submissionState.completed.length;
    const totalCount = submissionState.totalToSubmit;

    return 10 + (completedCount / totalCount) * 90;
  }, [submissionState.totalToSubmit, submissionState.completed.length]);

  const form = useForm<CreateMassSurveyFormValues>({
    defaultValues: {
      title: "",
      description: "",
      category: "other",
      startDate: smartDates.startDate,
      endDate: smartDates.endDate,
      thankYouMessage: "",
      questions: [{ type: "open", question: "" }],
      group: undefined,
      projects: [],
      internal: false,
    },
  });

  const {
    fields: questionFields,
    append: appendQuestion,
    remove: removeQuestion,
    update: updateQuestion,
    move: moveQuestion,
  } = useFieldArray({
    control: form.control,
    name: "questions",
  });

  const currProjects = useWatch({
    name: "projects",
    control: form.control,
  });
  const currGroup = useWatch({
    name: "group",
    control: form.control,
  });
  const start = useWatch({ control: form.control, name: "startDate" });

  const register = form.register;
  useEffect(() => {
    register("projects", {
      validate: (value) => {
        if (value.length === 0) {
          return t("page.mass-surveys.create.validation.projects-required");
        }
      },
    });
  }, [register, t]);

  const handleStartDateChange = (newStartDate: Date | undefined) => {
    const end = getEndDateAfterStartChange(start, form.getValues("endDate"), newStartDate, calculateSurveyEndDate);
    form.setValue("endDate", end, { shouldDirty: true, shouldValidate: true });
  };

  const handleCategoryChange = (newCategory: CreateMassSurveyFormValues["category"]) => {
    if (newCategory === "other") {
      form.reset();

      return;
    }

    for (let i = questionFields.length - 1; i >= 0; i--) {
      removeQuestion(i);
    }

    const projectName = sessionUser.project.name;

    form.setValue("title", t(feelingAtHomeTemplate.title, { projectName }));
    form.setValue("description", t(feelingAtHomeTemplate.description, { projectName }));
    form.setValue("thankYouMessage", t(feelingAtHomeTemplate.thankYouMessage, { projectName }));

    for (const element of feelingAtHomeTemplate.questions) {
      appendQuestion({
        type: element.type,
        question: t(element.title, { projectName }),
        options:
          "options" in element
            ? element.options?.map((option) => ({
                option: t(option.option, { projectName }),
              }))
            : [],
        useForAnalysis: "useForAnalysis" in element && element.useForAnalysis,
      });
    }
  };

  const handleSubmit = async (formValues: CreateMassSurveyFormValues) => {
    try {
      setSubmissionError(undefined);
      progressScreenHandler.setTrue();

      if (!formValues.startDate || !formValues.endDate) {
        throw new Error("Missing start or end date");
      }

      if (!formValues.questions.length) {
        throw new Error("No questions provided");
      }

      const totalProjects = formValues.projects.length;
      setSubmissionState({
        amountSubmitting: totalProjects,
        amountSuccess: 0,
        amountFailure: 0,
        totalToSubmit: totalProjects,
        completed: [],
        isSubmitting: true,
      });

      const data: CreateSurveyBulkRequest = {
        title: formValues.title,
        description: formValues.description,
        startDate: formValues.startDate.toISOString(),
        endDate: formValues.endDate.toISOString(),
        surveyCompleted: formValues.thankYouMessage,
        questions: formValues.questions.map((question, idx) => ({
          question: question.question,
          type: question.type,
          sorting: idx,
          offeredAnswers: question.options.map((option) => ({ answer: option.option })),
          useForAnalysis: !!question.useForAnalysis,
        })),
        projectIds: formValues.projects.map((project) => project.id),
        systemGroupId: formValues.group?.id,
        category: formValues.category,
        internal: formValues.internal,
      };

      const response = await addMassSurveyMutation.mutateAsync(data);

      if (!response.ok || !response.body) {
        setSubmissionState((currState) => ({ ...currState, isSubmitting: false }));
        throw new Error("Failed to retrieve upload progress");
      }

      await processJsonStream<CreateSurveyBulkItemDto>({
        stream: response.body,
        callbacks: {
          onChunk: (result) => {
            setSubmissionState((currState) => {
              const newCompleted = [...currState.completed, result];
              const newAmountSuccess = currState.amountSuccess + (result.status === "success" ? 1 : 0);
              const newAmountFailure = currState.amountFailure + (result.status === "error" ? 1 : 0);

              return {
                ...currState,
                amountSuccess: newAmountSuccess,
                amountFailure: newAmountFailure,
                completed: newCompleted,
              };
            });
          },
          onDone: () => {
            // Wait 1 second before finishing to show full progress bar
            setTimeout(() => {
              setSubmissionState((currState) => ({
                ...currState,
                amountSubmitting: 0,
                isSubmitting: false,
              }));
            }, 1000);
          },
          onError: () => {
            setSubmissionError(t("page.mass-surveys.create.error.create-failed"));
            setSubmissionState((currState) => ({
              ...currState,
              isSubmitting: false,
            }));
          },
        },
      });
    } catch (error) {
      console.error("Mass survey creation failed:", error);
      setSubmissionError(t("page.mass-surveys.create.error.create-failed"));
      setSubmissionState((currState) => ({ ...currState, isSubmitting: false }));
    }
  };

  const handleCreateSurvey = async () => {
    await handleSubmit(form.getValues());
    confirmModalHandler.setFalse();
  };

  const handleRetrySubmission = async () => {
    setSubmissionError(undefined);
    setSubmissionState({
      amountSubmitting: 0,
      amountSuccess: 0,
      amountFailure: 0,
      totalToSubmit: 0,
      completed: [],
      isSubmitting: false,
    });
    await handleSubmit(form.getValues());
  };

  const handleClose = () => {
    void navigate(routes.portfolio.overview());
  };

  const handleConfirmAudienceSelection = ({
    group,
    projects,
  }: {
    group: SystemGroupDto;
    projects: SimpleProjectDto[];
  }) => {
    form.setValue("group", group, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue("projects", projects, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const canLeaveFirstStep = () =>
    form.trigger(["projects", "group", "category", "startDate", "endDate", "title", "description", "thankYouMessage"]);
  const canLeaveSecondStep = () => form.trigger(["questions"]);

  if (isPendingPortfolioPermissions) {
    return <FullSizeLoader />;
  }

  if (errorPortfolioPermissions) {
    return <ErrorPage error={errorPortfolioPermissions} />;
  }

  if (!portfolioPermissions.canMassSurvey) {
    return <ErrorPage status={403} />;
  }

  if (isProgressScreenOpen) {
    return (
      <CreateMassSurveyProgressScreen
        submissionState={submissionState}
        progress={submissionProgress}
        error={submissionError}
        projects={currProjects}
        onClose={handleClose}
        onRetry={handleRetrySubmission}
      />
    );
  }

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        contentSize="constrained"
        tabTitle={t("page.mass-surveys.create.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.portfolio.title"),
              },
              {
                name: t("page.mass-surveys.create.title"),
              },
            ]}
          />
        }
      >
        <Form.Root formMethods={form} onSubmit={() => confirmModalHandler.setTrue()}>
          <Form.Container>
            <Wizard
              strictOrder
              isSubmitting={addMassSurveyMutation.isPending}
              onFinish={() => confirmModalHandler.setTrue()}
              actionsText={{
                prevStep: t("component.wizard.action.prev-step"),
                nextStep: t("component.wizard.action.next-step"),
                finish: t("page.mass-surveys.create.finish-button"),
              }}
            >
              <WizardStep
                id={1}
                canLeave={canLeaveFirstStep}
                hasFinish={false}
                title={t("component.wizard.step-general")}
              >
                <Form.Body>
                  <Notice
                    icon={<Translate01Icon />}
                    variant="special"
                    message={t("common.label.your-input-will-be-auto-translated")}
                  />
                  <FormField label={t("page.surveys.create-or-edit.form.title.label")} actions={flagIcon} required>
                    <FormInput<CreateMassSurveyFormValues>
                      name="title"
                      placeholder={t("page.surveys.create-or-edit.form.title.placeholder")}
                      rules={{
                        validate: {
                          required: createRequiredStringRule(t, "page.surveys.create-or-edit.form.title.label"),
                        },
                        maxLength: {
                          message: t("components.form.error.max-length", {
                            length: TITLE_MAX_LENGTH,
                          }),
                          value: TITLE_MAX_LENGTH,
                        },
                      }}
                    />
                  </FormField>
                  <FormField
                    label={t("page.surveys.create-or-edit.form.description.label")}
                    actions={flagIcon}
                    required
                  >
                    <FormTextArea<CreateMassSurveyFormValues>
                      name="description"
                      placeholder={t("page.surveys.create-or-edit.form.description.placeholder")}
                      rules={{
                        validate: {
                          required: createRequiredStringRule(t, "page.surveys.create-or-edit.form.description.label"),
                        },
                        maxLength: {
                          message: t("components.form.error.max-length", {
                            length: DESCRIPTION_MAX_LENGTH,
                          }),
                          value: DESCRIPTION_MAX_LENGTH,
                        },
                      }}
                    />
                  </FormField>
                  <FormField
                    label={t("page.surveys.create-or-edit.form.thanks-message.label")}
                    actions={flagIcon}
                    required
                  >
                    <FormTextArea<CreateMassSurveyFormValues>
                      name="thankYouMessage"
                      placeholder={t("page.surveys.create-or-edit.form.thanks-message.placeholder")}
                      rows={1}
                      rules={{
                        validate: {
                          required: createRequiredStringRule(
                            t,
                            "page.surveys.create-or-edit.form.thanks-message.label",
                          ),
                        },
                        maxLength: {
                          message: t("components.form.error.max-length", {
                            length: DESCRIPTION_MAX_LENGTH,
                          }),
                          value: DESCRIPTION_MAX_LENGTH,
                        },
                      }}
                    />
                  </FormField>
                  {sessionUser.isSuperAdmin && (
                    <FormField label={t("page.surveys.create-or-edit.form.category.label")} required>
                      <FormSelect<CreateMassSurveyFormValues, CreateMassSurveyFormValues["category"]>
                        name="category"
                        placeholder={t("page.surveys.create-or-edit.form.category.placeholder")}
                        items={surveyCategories}
                        onChange={(x) => handleCategoryChange(x!)}
                        keySelector={(x) => x}
                        renderOption={(x) => {
                          switch (x) {
                            case "other":
                              return t("page.surveys.categories.other");
                            case "feelAtHome":
                              return t("page.surveys.categories.feel-at-home");
                          }
                        }}
                        rules={{
                          required: t("components.form.error.required", {
                            inputName: t("page.surveys.create-or-edit.form.category.label"),
                          }),
                        }}
                      />
                    </FormField>
                  )}
                  <FormField label={t("page.surveys.create-or-edit.form.starts-at.label")} required>
                    <FormDatePicker<CreateMassSurveyFormValues>
                      data-testid="survey-start-date-input"
                      name="startDate"
                      id="startDate"
                      type="datetime"
                      min={minStart}
                      onChange={handleStartDateChange}
                      rules={{
                        validate: {
                          laterThanMin: (startDate) => {
                            if (startDate == null) {
                              return undefined;
                            }

                            return startDate < minStart
                              ? t("page.surveys.create-or-edit.form.starts-at.error.must-be-in-future")
                              : undefined;
                          },
                        },
                        required: {
                          message: t("components.form.error.required", {
                            inputName: t("page.surveys.create-or-edit.form.starts-at.label"),
                          }),
                          value: true,
                        },
                      }}
                    />
                  </FormField>
                  <FormField label={t("page.surveys.create-or-edit.form.ends-at.label")} required>
                    <FormDatePicker<CreateMassSurveyFormValues>
                      data-testid="survey-end-date-input"
                      name="endDate"
                      id="endDate"
                      type="datetime"
                      min={start ? addMinutes(start, 1) : minStart}
                      rules={{
                        validate: {
                          laterThanStart: (endDate, formValues) => {
                            if (endDate == null) {
                              return undefined;
                            }

                            return formValues.startDate && endDate <= formValues.startDate
                              ? t("page.surveys.create-or-edit.form.ends-at.error.later-than-start-date")
                              : undefined;
                          },
                        },
                        required: {
                          message: t("components.form.error.required", {
                            inputName: t("page.surveys.create-or-edit.form.ends-at.label"),
                          }),
                          value: true,
                        },
                      }}
                    />
                  </FormField>
                  <FormField label={t("page.admin-create-post.audience-selector.label")} required>
                    <CreateMassSurveyFormAudienceSelector
                      selectedGroup={currGroup}
                      selectedProjects={currProjects}
                      isError={!!form.formState.errors.projects}
                      onConfirmSelection={handleConfirmAudienceSelection}
                    />
                  </FormField>
                  {sessionUser.isSuperAdmin && (
                    <FormField label="">
                      <FormCheckbox<CreateMassSurveyFormValues>
                        name="internal"
                        label={t("page.surveys.create-or-edit.internal.label")}
                      />
                    </FormField>
                  )}
                </Form.Body>
              </WizardStep>

              <WizardStep
                id={2}
                canLeave={canLeaveSecondStep}
                hasFinish={true}
                title={t("page.surveys.create-or-edit.wizard.step-questions")}
              >
                <Form.Body>
                  <MassSurveyFormQuestions
                    questionFields={questionFields}
                    removeQuestion={removeQuestion}
                    updateQuestion={updateQuestion}
                    moveQuestion={moveQuestion}
                  />
                </Form.Body>
                <Form.Footer
                  renderPrimaryAction={() => (
                    <Button
                      data-testid="add-question-btn"
                      variant="ghostPrimary"
                      onClick={() =>
                        appendQuestion({ type: "open", question: "", options: [] }, { shouldFocus: false })
                      }
                      icon={<PlusIcon />}
                    >
                      {t("page.surveys.create-or-edit.question.add")}
                    </Button>
                  )}
                />
              </WizardStep>
            </Wizard>
          </Form.Container>
        </Form.Root>
      </DocumentPage>

      <ConfirmModal
        tone="primary"
        title={t("page.mass-surveys.create.cancel-modal.title")}
        description={t("page.mass-surveys.create.cancel-modal.description")}
        isOpened={isCancelModalOpened}
        shouldCloseOnEsc
        onReject={cancelModalHandler.setFalse}
        onOpenChange={cancelModalHandler.set}
        rejectBtnProps={{
          text: t("common.action.cancel"),
        }}
        onResolve={handleClose}
        resolveBtnProps={{
          text: t("common.action.confirm"),
        }}
        isLoading={false}
      />

      <ConfirmModal
        tone="primary"
        title={t("page.mass-surveys.create.confirm-modal.title")}
        description={t("page.mass-surveys.create.confirm-modal.description", {
          count: currProjects.length,
        })}
        isLoading={addMassSurveyMutation.isPending}
        onReject={confirmModalHandler.setFalse}
        onOpenChange={confirmModalHandler.set}
        onResolve={handleCreateSurvey}
        resolveBtnProps={{
          text: t("common.action.confirm"),
          "data-testid": "confirm-btn",
        }}
        isOpened={isConfirmModalOpen}
        data-testid="mass-survey-confirm-modal"
        shouldCloseOnEsc
      />
    </>
  );
}
